'use client'

import {
    ChevronsUpDown,
    CircleUserRound,
    KeyRound,
    LogOut
} from 'lucide-react'

import {
    Avatar,
    AvatarFallback,
    AvatarImage
} from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar
} from '@/components/ui/sidebar'
import SidebarData from '@/components/data/sidebar-data'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { logOut } from '@/store/slices/authSlice'
import { persistor } from '@/store/store'
import { ICON_SIZE_1 } from '@/utils/constant'
import AuthAPI from '@/api/resources/Auth.api'

export function NavUser() {
    const data = SidebarData()
    const { isMobile } = useSidebar()
    const getInitials = (name: string) => {
        if (!name || typeof name !== 'string') return 'N/A'
        return name
            .split(' ')
            .map(word => word.charAt(0).toUpperCase())
            .join('')
    }

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const handleLogout = async (e: React.MouseEvent) => {
        e.preventDefault()
        AuthAPI.logout()
        await dispatch(logOut())
        persistor.purge()
        navigate('/login')
    }

    return (
        <SidebarMenu className='px-2'>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarImage src={data.user.avatar} alt={data.user.fname} />
                                <AvatarFallback className="rounded-lg">{getInitials(data.user.fname)}</AvatarFallback>
                            </Avatar>
                            <div className="hidden sm:hidden lg:grid grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-medium">{data.user.fname}</span>
                                <span className="truncate text-xs">{data.user.email}</span>
                            </div>
                            <ChevronsUpDown size={ICON_SIZE_1} className="ml-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                        side={isMobile ? 'bottom' : 'bottom'}
                        align="end"
                        sideOffset={4}
                    >
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <Avatar className="h-8 w-8 rounded-lg">
                                    <AvatarImage src={data.user.avatar} alt={data.user.fname} />
                                    <AvatarFallback className="rounded-lg">{getInitials(data.user.fname)}</AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-medium">{data.user.fname}</span>
                                    <span className="truncate text-xs">{data.user.email}</span>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <Link to="/edit-profile">
                                <DropdownMenuItem className="cursor-pointer">
                                    <CircleUserRound size={ICON_SIZE_1} />
                                    {'Edit Profile'}
                                </DropdownMenuItem>
                            </Link>
                            <Link to="/change-password">
                                <DropdownMenuItem className="cursor-pointer">
                                    <KeyRound size={ICON_SIZE_1} />
                                    {'Change Password'}
                                </DropdownMenuItem>
                            </Link>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <Link to="/logout" onClick={handleLogout}>
                            <DropdownMenuItem className="cursor-pointer">
                                <LogOut size={ICON_SIZE_1} />
                                {'Log out'}
                            </DropdownMenuItem>
                        </Link>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    )
}
